import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

const baseUrl = '/secure/policies/purview/sensitivity-label'

export const purviewSensitivityLabelApi = createApi({
  reducerPath: 'purviewSensitivityLabel',
  tagTypes: ['SensitivityLabel', 'SensitivityLabels', 'SensitivityLabelsCount'],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getSensitivityLabels: builder.query({
      query: (customerId) => ({
        url: baseUrl,
        params: { customerId },
      }),
      transformResponse: (response) => response ?? [],
      providesTags: (result, error, customerId) => [{ type: 'SensitivityLabels', id: customerId }],
    }),
    getSensitivityLabelById: builder.query({
      query: ({ customerId, labelId }) => ({
        url: `${baseUrl}/${labelId}`,
        params: { customerId },
      }),
      transformResponse: (response) => {
        const { rawDataFlat, ...rest } = response ?? {}
        return { ...rawDataFlat, ...rest }
      },
      providesTags: (result, error, { labelId }) => [{ type: 'SensitivityLabel', id: labelId }],
    }),
    getSensitivityLabelsCount: builder.query({
      query: (customerId) => ({
        url: baseUrl,
        params: { customerId, countOnly: true },
      }),
      transformResponse: (response) => response?.count ?? 0,
      providesTags: (result, error, customerId) => [{ type: 'SensitivityLabelsCount', id: customerId }],
    }),
    createSensitivityLabel: builder.mutation({
      query: ({ customerId, payload }) => ({
        url: baseUrl,
        method: 'POST',
        params: { customerId },
        body: payload,
      }),
      invalidatesTags: (result, error, { customerId }) => [
        { type: 'SensitivityLabels', id: customerId },
        { type: 'SensitivityLabelsCount', id: customerId },
      ],
    }),
    deleteSensitivityLabel: builder.mutation({
      query: ({ customerId, policyId }) => ({
        url: `${baseUrl}/${policyId}`,
        method: 'DELETE',
        params: { customerId },
      }),
      invalidatesTags: (result, error, { customerId, policyId }) => [
        { type: 'SensitivityLabels', id: customerId },
        { type: 'SensitivityLabel', id: policyId },
        { type: 'SensitivityLabelsCount', id: customerId },
      ],
    }),
  }),
})

export const {
  useGetSensitivityLabelsQuery,
  useLazyGetSensitivityLabelsQuery,
  useGetSensitivityLabelByIdQuery,
  useLazyGetSensitivityLabelByIdQuery,
  useGetSensitivityLabelsCountQuery,
  useLazyGetSensitivityLabelsCountQuery,
  useCreateSensitivityLabelMutation,
  useDeleteSensitivityLabelMutation,
} = purviewSensitivityLabelApi

export const useGetSensitivityLabels = (options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetSensitivityLabelsQuery(customerId, { ...options, skip: !customerId || options.skip })
}

export const useGetSensitivityLabelById = ({ labelId }, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetSensitivityLabelByIdQuery(
    { customerId, labelId },
    { ...options, skip: !customerId || !labelId || options.skip },
  )
}
